import {Button, Card, ProgressBar} from 'platform/components';
import {Box, Center, Grid, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';

import {type MyDayData} from '../../../types/MyDayData';
import {getRecommendationSignalLabel} from '../../../utils/getRecommendationSignalLabel';
import {getRecommendationSignalReason} from '../../../utils/getRecommendationSignalReason';

interface MyDayPortfolioProps {
  data: MyDayData;
  onSelect: (interestId: string) => void;
  isWide: boolean;
}

export function MyDayPortfolio(props: MyDayPortfolioProps) {
  const risks = props.data.recommendations.filter((recommendation) =>
    recommendation.signals.some(
      (signal) => signal.code === 'goingCold' || signal.code === 'overdueTask'
    )
  );
  const stages = [
    {key: 'newInterests', label: i18n.t('page.homepage.salesFunnel.labels.newInterests')},
    {key: 'activeInterests', label: i18n.t('page.homepage.salesFunnel.labels.activeInterests')},
    {key: 'offers', label: i18n.t('page.homepage.salesFunnel.labels.offers')},
    {key: 'contracts', label: i18n.t('page.homepage.salesFunnel.labels.contracts')},
    {key: 'sold', label: i18n.t('page.homepage.salesFunnel.labels.sold')},
    {key: 'closed', label: i18n.t('page.homepage.salesFunnel.labels.closed')},
  ] satisfies {key: keyof NonNullable<MyDayData['saleFunnel']>; label: string}[];

  return (
    <Grid columns={props.isWide ? 2 : 1} spacing={4} align="flex-start">
      <Show when={props.data.saleFunnel}>
        {(funnel) => (
          <Card title={i18n.t('page.interestMyDay.stages.title')} titleIcon="action/trending_up">
            <Grid columns={[2, 3]} spacing={3}>
              {stages.map((stage) => (
                <Box
                  key={stage.key}
                  backgroundColor="palettes.neutral.10.100"
                  padding={3}
                  borderRadius="small"
                >
                  <VStack spacing={3}>
                    <Text color="secondary" size="small">
                      {stage.label}
                    </Text>
                    <Heading size={2} headingLevel={3}>
                      {funnel[stage.key].toString()}
                    </Heading>
                    <Box
                      backgroundColor="palettes.blue.20.100"
                      borderRadius="circular"
                      overflow="hidden"
                    >
                      <ProgressBar
                        percentage={funnel[stage.key] / Math.max(...Object.values(funnel), 1)}
                        small
                      />
                    </Box>
                  </VStack>
                </Box>
              ))}
            </Grid>
          </Card>
        )}
      </Show>
      <Card title={i18n.t('page.interestMyDay.risks.title')} titleIcon="alert/warning">
        <VStack spacing={4}>
          <Show when={risks.length === 0}>
            <Text>{i18n.t('general.labels.noData')}</Text>
          </Show>
          {risks.map((recommendation) => (
            <HStack
              key={recommendation.interestId}
              justify="space-between"
              align="flex-start"
              spacing={3}
              wrap
            >
              <Box
                width={8}
                height={8}
                flexShrink={0}
                backgroundColor="palettes.orange.10.100"
                borderRadius="small"
              >
                <Center height="100%">
                  <Icon value="alert/warning" size={4} color="text.warning" />
                </Center>
              </Box>
              <VStack spacing={2} grow={1}>
                <HStack>
                  <Button
                    variant="link"
                    size="small"
                    title={recommendation.customerLabel}
                    aria-label={recommendation.customerLabel}
                    rightIcon="navigation/arrow_forward"
                    onClick={() => props.onSelect(recommendation.interestId)}
                  />
                </HStack>
                {recommendation.signals
                  .filter((signal) => signal.code === 'goingCold' || signal.code === 'overdueTask')
                  .map((signal) => (
                    <VStack key={signal.code} spacing={1}>
                      <Text size="small">{getRecommendationSignalLabel(signal)}</Text>
                      <Text size="small" color="secondary">
                        {getRecommendationSignalReason(signal)}
                      </Text>
                    </VStack>
                  ))}
              </VStack>
            </HStack>
          ))}
          <Show when={props.data.hasMoreRecommendations}>
            <Text size="small" color="secondary">
              {i18n.t('page.interestMyDay.coverage.more')}
            </Text>
          </Show>
        </VStack>
      </Card>
    </Grid>
  );
}
